import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
    Box, Typography, Button, Stack, Dialog, IconButton, MenuItem, TextField, Chip, LinearProgress, CircularProgress, ListSubheader, InputAdornment,
} from '@mui/material';
import * as XLSX from 'xlsx';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import http, { apiErrorMessage } from '../../Api/http';
import { AddCandidate } from '../../Api/Api';

// Dark-purple surface for the import popup.
const D = {
    bg: '#FFFFFF', panel: '#FBFAFF', line: '#E6EAF1', text: '#0F172A', muted: '#64748B',
    accent: '#7C5CFC', accent2: '#9B87FB', soft: '#F1EEFE', softBorder: '#C9BEFB', ok: '#16A34A', warn: '#D97706', bad: '#DC2626',
};
// Every dropdown in this popup opens at the same fixed height and scrolls inside it.
const MENU_HEIGHT = 260;
const fieldDark = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px', fontSize: 13.5, color: D.text, bgcolor: '#fff',
        '& fieldset': { borderColor: '#E5E7EB' },
        '&:hover fieldset': { borderColor: D.softBorder },
        '&.Mui-focused fieldset': { borderColor: D.accent, borderWidth: 1.5 },
    },
};
const darkMenu = { slotProps: { paper: { sx: { bgcolor: '#fff', color: D.text, border: `1px solid ${D.line}`, maxHeight: MENU_HEIGHT, overflowY: 'auto', '& .MuiMenuItem-root:hover, & .MuiMenuItem-root.Mui-selected': { bgcolor: D.soft } } } } };
const btnSolid = { height: 42, px: 2.6, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: 13.5, color: '#fff', bgcolor: D.accent, boxShadow: `0 2px 6px ${D.accent}40`, '&:hover': { bgcolor: '#6246E0' }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF', boxShadow: 'none' } };
const btnGhost = { height: 42, px: 2.2, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: 13.5, color: '#475569', border: '1px solid #E5E7EB', bgcolor: '#fff', '&:hover': { bgcolor: '#F8FAFC' } };


// A dropdown with a search box pinned at the top of its list. options: [{ value, label, muted? }]; a muted option
// (e.g. "Don't import") is always shown, the rest are filtered by what is typed.
function SearchSelect({ value, onChange, options, searchPlaceholder = 'Search…' }) {
    const [q, setQ] = useState('');
    const needle = q.trim().toLowerCase();
    const shown = options.filter((o) => o.muted || !needle || String(o.label).toLowerCase().includes(needle));
    const hasMatch = shown.some((o) => !o.muted);
    return (
        <TextField
            select fullWidth size="small" value={value} onChange={onChange} sx={fieldDark}
            slotProps={{
                select: {
                    displayEmpty: true,
                    MenuProps: { autoFocus: false, slotProps: { ...darkMenu.slotProps, transition: { onExited: () => setQ('') } } },
                    renderValue: (v) => options.find((o) => String(o.value) === String(v))?.label ?? '',
                },
            }}
        >
            <ListSubheader sx={{ bgcolor: '#fff', p: 1, lineHeight: 'normal' }}>
                <TextField
                    autoFocus size="small" fullWidth placeholder={searchPlaceholder} value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation(); }}
                    sx={fieldDark}
                    slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: D.muted }} /></InputAdornment> } }}
                />
            </ListSubheader>
            {shown.map((o) => (
                <MenuItem key={String(o.value)} value={o.value} sx={{ fontSize: 13, ...(o.muted && { color: D.muted }) }}>{o.label}</MenuItem>
            ))}
            {!hasMatch && needle && <MenuItem disabled sx={{ fontSize: 13 }}>No matches</MenuItem>}
        </TextField>
    );
}

const MAX_ROWS = 500;
// "20K" -> 20000, "13k" -> 13000, "4 LPA" / "3.5 lakh" -> 400000 / 350000, "1.2 cr" -> 12000000, "25,000" -> 25000.
// A range such as "10-12K" uses the higher figure with the shared unit.
const parseMoney = (v) => {
    const t = String(v ?? '').toLowerCase().replace(/,/g, '');
    const range = /(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/.exec(t);
    const m = range ? { 0: range[0], 1: String(Math.max(Number(range[1]), Number(range[2]))), index: range.index } : /(\d+(?:\.\d+)?)/.exec(t);
    if (!m) return 0;
    const n = Number(m[1]);
    const rest = t.slice(m.index + m[0].length).trim();
    if (/^(k|thousand)/.test(rest)) return Math.round(n * 1000);
    if (/^(l|lac|lakh|lakhs|lpa)/.test(rest)) return Math.round(n * 100000);
    if (/^(cr|crore)/.test(rest)) return Math.round(n * 10000000);
    return Math.round(n);
};
// "2 Years", "1.5 yrs", "4 Months", "2+", "3-5 years" -> years as a number. "Fresher" or nothing -> 0.
const parseYears = (v) => {
    // A four-digit year ("Fresher 2026 passedout", "1 Year - 2017") is a date, not a length of experience.
    const t = String(v ?? '').toLowerCase().replace(/(^|[^0-9])(19|20)[0-9]{2}(?![0-9])/g, ' ');
    const m = /(\d+(?:\.\d+)?)/.exec(t);
    if (!m) return 0;
    const n = Number(m[1]);
    const years = Math.round((/month|mos?(?![a-z])/.test(t) ? n / 12 : n) * 10) / 10;
    return years > 60 ? 0 : years;
};
const LOG_KEYS = ['remarks', 'designation', 'currentSalary', 'interviewDate'];
// A date cell can arrive as an Excel serial number; show it as dd-mm-yyyy.
const plainDate = (v) => {
    const n = Number(v);
    if (!v || !Number.isFinite(n) || n < 20000 || n > 80000) return String(v ?? '').trim();
    const d = new Date(Math.round((n - 25569) * 86400 * 1000));
    return `${String(d.getUTCDate()).padStart(2, '0')}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${d.getUTCFullYear()}`;
};
const COMM_TYPES = ['Call', 'Email', 'WhatsApp', 'SMS', 'In-person'];
const COMM_STATUSES = ['Connected', 'No Response', 'Follow-up Scheduled', 'Not Interested'];
// The server treats a blank email as one shared address, so only one candidate per vacancy can have none.
// Until that is fixed there, a row without an email gets a unique placeholder built from the phone number.
const PLACEHOLDER_DOMAIN = 'noemail.local';
const NOTICE_MAX = 30;
// The server rejects a long company name (a whole job history was rejected at 142 characters; 95 was accepted). A longer
// one is shortened on the candidate and kept in full in the communication note.
const COMPANY_MAX = 100;
const placeholderEmail = (r) => `${digits(r.phone) || 'row'}-${r.line}-${Date.now().toString(36)}@${PLACEHOLDER_DOMAIN}`;
// The Source card sets one of the existing source names for every row.
const FIXED = 'set:';

// Candidate fields a file column can be mapped to. `hints` auto-match the file's header names.
const FIELDS = [
    { key: 'name', label: 'Full name', required: true, hints: ['full name', 'name', 'candidate', 'candidate name'] },
    { key: 'location', label: 'City', hints: ['location', 'city', 'place', 'area', 'current location'] },
    { key: 'phone', label: 'Phone', hints: ['phone', 'mobile', 'contact', 'contact no', 'contact number', 'phone no', 'phone number', 'mobile no', 'mobile number'] },
    { key: 'currentCompany', label: 'Current company', hints: ['current company', 'company', 'company name', 'employer', 'organisation', 'organization'] },
    { key: 'experience', label: 'Experience (years)', hints: ['experience', 'experience in bd', 'experience years', 'exp', 'years of experience'] },
    { key: 'expectedSalary', label: 'Expected salary', hints: ['expected salary', 'expected ctc', 'ectc', 'salary'] },
    { key: 'noticePeriod', label: 'Notice period', hints: ['notice period', 'notice'] },
    { key: 'designation', label: 'Designation → communication log', hints: ['designation', 'current designation', 'job title'] },
    { key: 'currentSalary', label: 'Current salary → communication log', hints: ['current salary', 'current ctc', 'present salary'] },
    { key: 'interviewDate', label: 'Interview date → communication log', hints: ['interview date'] },
    { key: 'remarks', label: 'Other remarks → communication log', hints: ['other remarks', 'remarks', 'remark', 'comments', 'notes'] },
    { key: 'source', label: 'Source', hints: ['source', 'lead source', 'channel'] },
];

const STEPS = [{ key: 'upload', label: 'Upload', sub: 'Select your file' }, { key: 'mapping', label: 'Mapping', sub: 'Match columns' }, { key: 'review', label: 'Review', sub: 'Confirm & import' }];

// A candidate sheet has a name column plus a contact or experience column; other sheets are ignored.
const looksLikeCandidates = (headers) => {
    const h = headers.map((x) => String(x ?? '').trim().toLowerCase());
    return h.some((x) => x.includes('name')) && h.some((x) => x.includes('contact') || x.includes('phone') || x.includes('experience'));
};

const norm = (s) => String(s ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const digits = (s) => String(s ?? '').replace(/\D/g, '').slice(-10);
const emailOk = (s) => /^\S+@\S+\.\S+$/.test(String(s).trim());
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const TEMPLATE_HEADERS = ['Full name', 'Email', 'Phone', 'Current company', 'Experience (years)', 'Expected salary', 'Notice period', 'Source', 'Vacancy / role'];
const TEMPLATE_ROW = ['Asha Rao', 'asha@example.com', '9876543210', 'Acme Ltd', 3, 600000, '30 days', 'LinkedIn', 'Frontend Developer'];

const downloadTemplate = (type) => {
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, TEMPLATE_ROW]);
    ws['!cols'] = TEMPLATE_HEADERS.map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Candidates');
    XLSX.writeFile(wb, type === 'csv' ? 'candidates_template.csv' : 'candidates_template.xlsx', type === 'csv' ? { bookType: 'csv' } : undefined);
};

const autoMap = (headers) => {
    const map = {};
    const used = new Set();
    FIELDS.forEach((f) => {
        const idx = headers.findIndex((h, i) => !used.has(i) && f.hints.includes(norm(h)));
        if (idx >= 0) { map[f.key] = String(idx); used.add(idx); } else map[f.key] = '';
    });
    return map;
};

export default function ImportCandidatesDialog({ open, onClose, vacancies, candidates, sources: SOURCES, onImported }) {
    const [step, setStep] = useState(0);
    const [file, setFile] = useState(null);
    const [sheets, setSheets] = useState([]); // every usable sheet (tab) in the workbook
    const [sheetIdx, setSheetIdx] = useState(0);
    const parsed = sheets[sheetIdx] || { headers: [], rows: [] };
    const [parseError, setParseError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [mapping, setMapping] = useState({});
    const [defaultVacancy, setDefaultVacancy] = useState('');
    const [moreFields, setMoreFields] = useState(false); // reveal the fields this sheet has no column for
    const [remarkType, setRemarkType] = useState('Call'); // how imported remarks are logged
    const [remarkStatus, setRemarkStatus] = useState('Connected');
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState(null);
    const input = useRef(null);

    useEffect(() => {
        if (open) { setStep(0); setFile(null); setSheets([]); setSheetIdx(0); setParseError(''); setMapping({}); setMoreFields(false); setDefaultVacancy(''); setImporting(false); setProgress(0); setResult(null); }
    }, [open]);

    // Switching tab re-matches the columns to that tab's headings. A tab named like an open vacancy
    // also pre-selects that vacancy; any other tab leaves the vacancy to be chosen.
    const chooseSheet = (i, list = sheets) => {
        const sh = list[i];
        if (!sh) return;
        setSheetIdx(i);
        setMoreFields(false);
        const auto = autoMap(sh.headers);
        Object.keys(auto).forEach((k) => {
            if (auto[k] !== '' && !sh.rows.some((r) => String(r[Number(auto[k])] ?? '').trim() !== '')) auto[k] = '';
        });
        setMapping(auto);
        const vac = vacancies.find((v) => norm(v.title) === norm(sh.name));
        setDefaultVacancy(vac ? vac.id : '');
    };

    const readFile = async (f) => {
        if (!f) return;
        setParseError('');
        if (!/\.(csv|xlsx|xls)$/i.test(f.name)) { setParseError('Choose a .csv, .xlsx or .xls file.'); return; }
        try {
            const wb = XLSX.read(await f.arrayBuffer(), { type: 'array', cellDates: false });
            // Every tab with data becomes a choice. The heading row is the first row (within the top 10)
            // with at least two filled cells, so a title line above the table doesn't break the mapping.
            const found = wb.SheetNames.map((name) => {
                const aoa = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '', blankrows: false });
                let h = aoa.slice(0, 10).findIndex((r) => r.filter((c) => String(c).trim() !== '').length >= 2);
                if (h < 0) h = 0;
                const headers = (aoa[h] || []).map((x) => String(x ?? '').trim());
                const rows = aoa.slice(h + 1).filter((r) => r.some((c) => String(c).trim() !== ''));
                return { name, headers, rows };
            }).filter((sh) => sh.headers.some(Boolean) && sh.rows.length > 0 && looksLikeCandidates(sh.headers));
            if (found.length === 0) { setParseError('No sheet in this file looks like a candidate list. A candidate sheet needs a Name column and a Contact, Phone or Experience column.'); return; }
            setFile(f); setSheets(found); chooseSheet(0, found);
        } catch {
            setParseError('Could not read that file. Check it is a valid spreadsheet.');
        }
    };

    const cell = (row, key) => {
        const m = mapping[key];
        if (m === '' || m == null) return '';
        if (String(m).startsWith(FIXED)) return String(m).slice(FIXED.length);
        return String(row[Number(m)] ?? '').trim();
    };

    // Every row, resolved against the mapping, with its status.
    const prepared = useMemo(() => {
        if (step < 2) return [];
        const seen = new Set();
        const existing = candidates || [];
        return parsed.rows.map((row, i) => {
            const rec = {
                line: i + 2,
                name: cell(row, 'name'), email: cell(row, 'email'), phone: cell(row, 'phone'), currentCompany: cell(row, 'currentCompany'),
                experience: parseYears(cell(row, 'experience')), expectedSalary: parseMoney(cell(row, 'expectedSalary')),
                noticePeriod: cell(row, 'noticePeriod'), remarks: cell(row, 'remarks'), location: cell(row, 'location'), designation: cell(row, 'designation'), currentSalary: cell(row, 'currentSalary'), interviewDate: plainDate(cell(row, 'interviewDate')),
                source: SOURCES.find((s) => norm(s) === norm(cell(row, 'source'))) || 'Other',
            };
            const vacText = cell(row, 'vacancy');
            const vac = (vacText && vacancies.find((v) => norm(v.title) === norm(vacText))) || vacancies.find((v) => String(v.id) === String(defaultVacancy));
            rec.vacancy = vac || null;
            if (!rec.name) return { ...rec, status: 'error', note: 'Name is missing' };
            if (rec.email && !emailOk(rec.email)) return { ...rec, status: 'error', note: 'Email is not valid' };
            if (!vac) return { ...rec, status: 'error', note: vacText ? `No open vacancy named "${vacText}"` : 'No vacancy chosen' };
            const e = norm(rec.email); const p = digits(rec.phone);
            const clash = (e && seen.has(`${vac.id}|e|${e}`)) || (p.length >= 7 && seen.has(`${vac.id}|p|${p}|${norm(rec.name)}`));
            // A row with no usable email or phone can only be recognised by name, so a re-run of the same sheet still
            // skips the people already added and imports just the missing ones. Two people with the same name are kept apart
            // when both have a location and the locations differ.
            // The same phone number under a different name is a different person (shared or mistyped numbers are common in
            // these sheets), so a phone only marks a repeat when the name matches too.
            const nameOnly = !e && p.length < 7;
            const dupExisting = existing.some((c) => String(c.vacancyId) === String(vac.id) && (
                (e && norm(c.email) === e) || (p.length >= 7 && digits(c.phone) === p && norm(c.name) === norm(rec.name)) || (nameOnly && norm(c.name) === norm(rec.name) && (!rec.location || !c.city || norm(c.city) === norm(rec.location)))
            ));
            if (e) seen.add(`${vac.id}|e|${e}`);
            if (p.length >= 7) seen.add(`${vac.id}|p|${p}|${norm(rec.name)}`);
            if (dupExisting) return { ...rec, status: 'duplicate', note: 'Already added' };
            if (clash) return { ...rec, status: 'duplicate', note: 'Repeated in this file' };
            return { ...rec, status: 'ready', note: '' };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, parsed, mapping, defaultVacancy, vacancies, candidates]);

    const noEmailCount = prepared.filter((r) => r.status === 'ready' && !r.email).length;
    const counts = useMemo(() => ({
        ready: prepared.filter((r) => r.status === 'ready').length,
        duplicate: prepared.filter((r) => r.status === 'duplicate').length,
        error: prepared.filter((r) => r.status === 'error').length,
    }), [prepared]);

    const mappedCols = new Set(Object.values(mapping).filter((v) => v !== '' && v != null && !String(v).startsWith(FIXED)).map(Number));
    const unusedCols = parsed.headers.map((h, i) => ({ h, i })).filter((c) => c.h && !mappedCols.has(c.i));
    const nameMapped = mapping.name !== '' && mapping.name != null;
    const vacancyOk = Boolean(defaultVacancy);
    const tooMany = parsed.rows.length > MAX_ROWS;
    const canContinue = step === 0 ? Boolean(file) : step === 1 ? nameMapped && vacancyOk && !tooMany : counts.ready > 0 && !importing;

    const runImport = async () => {
        const todo = prepared.filter((r) => r.status === 'ready');
        setImporting(true); setProgress(0);
        const failed = [];
        let done = 0;
        for (const r of todo) {
            try {
                // The server only stores a short notice period (about 30 characters); a longer one is rejected. The short
                // part is saved on the candidate and the full text is kept in the communication note below.
                const longNotice = String(r.noticePeriod || '').trim();
                const noticeShort = longNotice.length > NOTICE_MAX ? longNotice.split('(')[0].trim().slice(0, NOTICE_MAX).trim() : longNotice;
                const companyFull = String(r.currentCompany || '').replace(/\s+/g, ' ').trim();
                const companyShort = companyFull.length > COMPANY_MAX ? `${companyFull.slice(0, COMPANY_MAX - 1).trim()}…` : companyFull;
                const payload = {
                    vacancyId: r.vacancy.id, fullName: r.name, email: r.email || placeholderEmail(r), phone: r.phone, currentCompany: companyShort,
                    experience: r.experience, expectedSalary: r.expectedSalary, noticePeriod: noticeShort, source: r.source,
                };
                if (r.location) { payload.location = r.location; payload.city = r.location; }
                // What the candidate record has no field for is kept in the communication log, so it is visible on the candidate.
                const note = [
                    r.remarks, r.designation && `Designation: ${r.designation}`, r.currentSalary && `Current salary: ${r.currentSalary}`,
                    r.interviewDate && `Interview date: ${r.interviewDate}`, r.location && `Location: ${r.location}`,
                    longNotice.length > NOTICE_MAX && `Notice period: ${longNotice}`,
                    companyFull.length > COMPANY_MAX && `Company: ${companyFull}`,
                ].filter(Boolean).join(' · ');
                if (note) payload.communications = [{ communicationType: remarkType, status: remarkStatus, description: note.slice(0, 1000) }];
                // A request that gets no reply at all (dropped connection, server busy) is tried again up to 3 times before
                // the row is called failed. If a retry is answered with "already exists", the first attempt did go through.
                let body = null;
                for (let attempt = 0; attempt < 4; attempt += 1) {
                    try {
                        ({ data: body } = await http.post(AddCandidate, payload));
                        break;
                    } catch (err) {
                        if (err?.response) {
                            if (attempt > 0 && err.response.status === 409) { body = {}; break; }
                            throw err;
                        }
                        if (attempt === 3) throw err;
                        await new Promise((res) => { setTimeout(res, 800 * (attempt + 1)); });
                    }
                }
                if (body?.error) throw new Error(body.message || 'Rejected');
                done += 1;
            } catch (err) {
                failed.push({ ...r, note: err?.response || err?.request ? apiErrorMessage(err, 'Could not add') : err.message });
            }
            setProgress(Math.round(((done + failed.length) / todo.length) * 100));
        }
        setImporting(false);
        setResult({ added: done, failed, skipped: counts.duplicate + counts.error });
        if (done > 0) onImported?.(done);
    };

    const next = () => { if (step < 2) setStep(step + 1); else runImport(); };

    const stepTitle = result ? 'Import complete' : ['Upload your file', 'Map your columns', 'Review & import'][step];
    const stepHint = result ? 'Here is what happened.' : [
        'Drop a CSV or Excel spreadsheet to get started',
        'Choose the sheet and vacancy, then match each field to a column',
        'Check the rows before they are added',
    ][step];

    return (
        <Dialog
            open={open} onClose={() => !importing && onClose()} maxWidth={false}
            slotProps={{
                backdrop: { sx: { backgroundColor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)' } },
                paper: { sx: { width: 'min(1040px, 96vw)', height: 'min(760px, 94vh)', m: 1, borderRadius: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'row', bgcolor: D.bg, color: D.text, border: `1px solid ${D.line}`, boxShadow: '0 30px 70px -20px rgba(15,23,42,0.35)' } },
            }}
        >
            {/* Left rail: the process (vertical steps) */}
            <Box sx={{ width: 250, flexShrink: 0, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', p: 2.4, bgcolor: '#F7F6FD', borderRight: `1px solid ${D.line}` }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: D.muted, mb: 2 }}>PROCESS</Typography>
                <Box>
                    {STEPS.map((s2, i) => {
                        const on = i === step && !result; const past = i < step || Boolean(result);
                        return (
                            <Stack key={s2.key} direction="row" spacing={1.4} sx={{ position: 'relative', pb: i < STEPS.length - 1 ? 3 : 0 }}>
                                {i < STEPS.length - 1 && <Box sx={{ position: 'absolute', left: 14, top: 30, bottom: 2, width: 2, borderRadius: 2, bgcolor: past ? D.ok : D.softBorder }} />}
                                <Box sx={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, fontWeight: 800, zIndex: 1,
                                    bgcolor: past ? D.ok : on ? D.accent : '#fff', color: past || on ? '#fff' : D.muted, border: past || on ? 'none' : `1.5px solid ${D.softBorder}`, boxShadow: on ? `0 0 0 4px ${D.soft}` : 'none' }}>
                                    {past ? <CheckRoundedIcon sx={{ fontSize: 17 }} /> : i + 1}
                                </Box>
                                <Box sx={{ pt: 0.2 }}>
                                    <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: on || past ? D.text : D.muted, lineHeight: 1.2 }}>{s2.label}</Typography>
                                    <Typography sx={{ fontSize: 11.5, color: D.muted }}>{s2.sub}</Typography>
                                </Box>
                            </Stack>
                        );
                    })}
                </Box>


            </Box>

            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            {/* Header: title, close */}
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2.5, px: 3, py: 2, borderBottom: `1px solid ${D.line}` }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
                    <Box sx={{ width: 42, height: 42, borderRadius: '12px', background: `linear-gradient(135deg, ${D.accent}, ${D.accent2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px -6px rgba(124,92,252,0.8)', flexShrink: 0 }}>
                        <CloudUploadRoundedIcon sx={{ color: '#fff' }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 17, fontWeight: 800, color: D.text, lineHeight: 1.2 }}>Import Candidates</Typography>
                        <Typography sx={{ fontSize: 11.5, color: D.muted }}>CSV / Excel</Typography>
                    </Box>
                </Stack>

                <IconButton onClick={onClose} disabled={importing} sx={{ color: D.muted, bgcolor: '#F1F5F9', '&:hover': { bgcolor: '#E2E8F0', color: '#fff' } }}>
                    <CloseRoundedIcon sx={{ fontSize: 19 }} />
                </IconButton>
            </Stack>

            {/* Body */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 2.4, '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(167,139,250,0.3)', borderRadius: 4 } }}>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: D.text, letterSpacing: '-0.3px' }}>{stepTitle}</Typography>
                <Typography sx={{ fontSize: 13, color: D.muted, mt: 0.3, mb: 2.2 }}>{stepHint}</Typography>

                {result ? (
                    <Box>
                        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                            {[['Added', result.added, D.ok], ['Skipped', result.skipped, D.warn], ['Failed', result.failed.length, D.bad]].map(([k, v, c]) => (
                                <Box key={k} sx={{ flex: 1, p: 2, borderRadius: '14px', bgcolor: D.panel, border: `1px solid ${D.line}`, borderTop: `3px solid ${c}` }}>
                                    <Typography sx={{ fontSize: 30, fontWeight: 800, color: c, lineHeight: 1 }}>{v}</Typography>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: D.muted, mt: 0.6 }}>{k}</Typography>
                                </Box>
                            ))}
                        </Stack>
                        {result.failed.length > 0 && (
                            <Box sx={{ border: `1px solid ${D.line}`, borderRadius: '12px', overflow: 'hidden', bgcolor: D.panel }}>
                                {result.failed.map((r) => (
                                    <Stack key={r.line} direction="row" sx={{ justifyContent: 'space-between', gap: 2, px: 1.8, py: 1.1, borderBottom: `1px solid ${D.line}`, '&:last-child': { borderBottom: 0 } }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: D.text }}>Row {r.line} · {r.name}</Typography>
                                        <Typography sx={{ fontSize: 12.5, color: D.bad, textAlign: 'right' }}>{r.note}</Typography>
                                    </Stack>
                                ))}
                            </Box>
                        )}
                    </Box>
                ) : step === 0 ? (
                    <Box>
                        <input ref={input} type="file" hidden accept=".csv,.xlsx,.xls" onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
                        <Box
                            onClick={() => input.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => { e.preventDefault(); setDragOver(false); readFile(e.dataTransfer.files?.[0]); }}
                            sx={{
                                height: 300, borderRadius: '20px', cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                border: `2px dashed ${file || dragOver ? D.accent2 : D.softBorder}`, bgcolor: file || dragOver ? D.soft : '#FBFAFF',
                                transition: 'all .2s', '&:hover': { borderColor: D.accent2, bgcolor: D.soft },
                            }}
                        >
                            <Box sx={{ width: 84, height: 84, borderRadius: '24px', background: `linear-gradient(135deg, ${D.accent}, ${D.accent2})`, boxShadow: '0 10px 24px -10px rgba(124,92,252,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                {file ? <DescriptionRoundedIcon sx={{ fontSize: 40, color: '#fff' }} /> : <CloudUploadRoundedIcon sx={{ fontSize: 40, color: '#fff' }} />}
                            </Box>
                            {file ? (
                                <>
                                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: D.text }}>{file.name}</Typography>
                                    <Typography sx={{ fontSize: 12.5, color: D.muted, mt: 0.4 }}>{fmtSize(file.size)} · {sheets.length > 1 ? `${sheets.length} sheets` : `${parsed.rows.length} rows`} · click to change</Typography>
                                    <Chip label="FILE READY" size="small" sx={{ mt: 1.6, fontWeight: 800, fontSize: 11, letterSpacing: 0.5, bgcolor: '#DCFCE7', color: D.ok, border: '1px solid #BBF7D0' }} />
                                </>
                            ) : (
                                <>
                                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: D.text }}>Drag a file here, or click to browse</Typography>
                                    <Typography sx={{ fontSize: 12.5, color: D.muted, mt: 0.4 }}>.csv, .xlsx or .xls · up to {MAX_ROWS} rows per sheet</Typography>
                                </>
                            )}
                        </Box>
                        {parseError && <Typography sx={{ fontSize: 12.5, color: D.bad, mt: 1.4, fontWeight: 600 }}>{parseError}</Typography>}
                        <Stack direction="row" spacing={2} sx={{ justifyContent: 'center', alignItems: 'center', mt: 2.2 }}>
                            <Typography sx={{ fontSize: 12.5, color: D.muted }}>Don&apos;t have a file?</Typography>
                            <Button size="small" startIcon={<FileDownloadRoundedIcon sx={{ fontSize: 16 }} />} onClick={() => downloadTemplate('csv')} sx={{ textTransform: 'none', fontWeight: 700, color: D.accent2 }}>CSV template</Button>
                            <Button size="small" startIcon={<FileDownloadRoundedIcon sx={{ fontSize: 16 }} />} onClick={() => downloadTemplate('xlsx')} sx={{ textTransform: 'none', fontWeight: 700, color: D.accent2 }}>Excel template</Button>
                        </Stack>
                    </Box>
                ) : step === 1 ? (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.2} sx={{ alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: sheets.length > 1 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' }, gap: 1.4, mb: 2.2 }}>
                                {sheets.length > 1 && (
                                    <Box>
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: D.text, mb: 0.7 }}>Sheet <Box component="span" sx={{ color: D.muted, fontWeight: 500 }}>· {sheets.length} in this file</Box></Typography>
                                        <SearchSelect
                                            value={sheetIdx} onChange={(e) => chooseSheet(Number(e.target.value))} searchPlaceholder="Search sheets…"
                                            options={sheets.map((sh, i) => ({ value: i, label: `${sh.name.trim()} · ${sh.rows.length} row${sh.rows.length === 1 ? '' : 's'}${vacancies.some((v) => norm(v.title) === norm(sh.name)) ? '' : ' · no vacancy'}` }))}
                                        />
                                    </Box>
                                )}
                                <Box>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: D.text, mb: 0.7 }}>Vacancy <Box component="span" sx={{ color: D.muted, fontWeight: 500 }}>· {parsed.rows.length} rows, {parsed.headers.filter(Boolean).length} columns</Box></Typography>
                                    <SearchSelect
                                        value={defaultVacancy} onChange={(e) => setDefaultVacancy(e.target.value)} searchPlaceholder="Search vacancies…"
                                        options={[{ value: '', label: 'Choose a vacancy', muted: true }, ...vacancies.map((v) => ({ value: v.id, label: `${v.title}${v.department ? ` · ${v.department}` : ''}` }))]}
                                    />
                                </Box>
                            </Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: D.muted, mb: 1.2 }}>MATCH FIELDS TO COLUMNS</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.1 }}>
                                {FIELDS.filter((f) => moreFields || f.key === 'name' || f.key === 'source' || (mapping[f.key] !== '' && mapping[f.key] != null)).map((f) => {
                                    const idx = mapping[f.key];
                                    const mapped = idx !== '' && idx != null;
                                    const fixedPick = mapped && String(idx).startsWith(FIXED);
                                    const sample = fixedPick ? `every row: ${String(idx).slice(FIXED.length)}` : mapped ? String(parsed.rows[0]?.[Number(idx)] ?? '') : '';
                                    return (
                                        <Box key={f.key} sx={{ minWidth: 0, p: 1.2, borderRadius: '10px', bgcolor: D.panel, border: `1px solid ${mapped ? D.softBorder : D.line}` }}>
                                            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
                                                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: D.text }}>{f.label}{f.required && <Box component="span" sx={{ color: '#FB7185', ml: 0.4 }}>*</Box>}</Typography>
                                                {mapped && <CheckRoundedIcon sx={{ fontSize: 16, color: D.ok }} />}
                                            </Stack>
                                            <SearchSelect
                                                value={idx ?? ''} onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))} searchPlaceholder="Search columns…"
                                                options={[
                                                    { value: '', label: f.key === 'source' ? '— Don\'t import (saved as Other) —' : '— Don\'t import —', muted: true },
                                                    ...(f.key === 'source'
                                                        ? SOURCES.map((x) => ({ value: `${FIXED}${x}`, label: x }))
                                                        : parsed.headers.map((h, i) => ({ value: String(i), label: h || `Column ${i + 1}` }))),
                                                ]}
                                            />
                                            {sample && <Typography noWrap sx={{ fontSize: 11, color: D.muted, mt: 0.5 }}>e.g. {sample}</Typography>}
                                        </Box>
                                    );
                                })}
                            </Box>
                            {!moreFields && FIELDS.some((f) => f.key !== 'name' && f.key !== 'source' && (mapping[f.key] === '' || mapping[f.key] == null)) && (
                                <Button size="small" onClick={() => setMoreFields(true)} sx={{ mt: 1, textTransform: 'none', fontWeight: 700, color: D.accent }}>
                                    + Map other fields ({FIELDS.filter((f) => f.key !== 'name' && f.key !== 'source' && (mapping[f.key] === '' || mapping[f.key] == null)).map((f) => f.label.split(' → ')[0]).join(', ')})
                                </Button>
                            )}
                            {LOG_KEYS.some((k) => mapping[k] !== '' && mapping[k] != null) && (
                                <Box sx={{ mt: 1.4, p: 1.4, borderRadius: '10px', bgcolor: D.soft, border: `1px solid ${D.softBorder}` }}>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: D.text, mb: 0.9 }}>Log these in each candidate&apos;s communication log as</Typography>
                                    <Stack direction="row" spacing={1.2}>
                                        <TextField select fullWidth size="small" value={remarkType} onChange={(e) => setRemarkType(e.target.value)} sx={fieldDark} slotProps={{ select: { MenuProps: darkMenu } }}>
                                            {COMM_TYPES.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
                                        </TextField>
                                        <TextField select fullWidth size="small" value={remarkStatus} onChange={(e) => setRemarkStatus(e.target.value)} sx={fieldDark} slotProps={{ select: { MenuProps: darkMenu } }}>
                                            {COMM_STATUSES.filter((t) => t !== 'Follow-up Scheduled').map((t) => <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
                                        </TextField>
                                    </Stack>
                                </Box>
                            )}
                            {unusedCols.length > 0 && (
                                <Typography sx={{ fontSize: 11.5, color: D.muted, mt: 1.4, lineHeight: 1.6 }}>
                                    Not imported (no matching field): {unusedCols.map((c) => c.h).join(', ')}.
                                </Typography>
                            )}
                            {tooMany && <Typography sx={{ fontSize: 12.5, color: D.bad, mt: 1.4, fontWeight: 600 }}>This sheet has {parsed.rows.length} rows. The limit is {MAX_ROWS} per import; pick another sheet or split it.</Typography>}
                            {!nameMapped && <Typography sx={{ fontSize: 12.5, color: D.bad, mt: 1.4, fontWeight: 600 }}>Map the Full name field to continue.</Typography>}
                            {nameMapped && !vacancyOk && <Typography sx={{ fontSize: 12.5, color: D.bad, mt: 1.4, fontWeight: 600 }}>Choose a vacancy to continue.</Typography>}
                        </Box>
                    </Stack>
                ) : (
                    <Box>
                        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                            {[['Ready to import', counts.ready, D.ok], ['Duplicates · skipped', counts.duplicate, D.warn], ['Errors · skipped', counts.error, D.bad]].map(([k, v, c]) => (
                                <Box key={k} sx={{ flex: 1, p: 1.8, borderRadius: '14px', bgcolor: D.panel, border: `1px solid ${D.line}`, borderTop: `3px solid ${c}` }}>
                                    <Typography sx={{ fontSize: 28, fontWeight: 800, color: c, lineHeight: 1 }}>{v}</Typography>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: D.muted, mt: 0.6 }}>{k}</Typography>
                                </Box>
                            ))}
                        </Stack>
                        <Box sx={{ border: `1px solid ${D.line}`, borderRadius: '14px', overflow: 'hidden', bgcolor: D.panel }}>
                            <Stack direction="row" sx={{ px: 1.8, py: 1, bgcolor: D.soft, fontSize: 10.5, fontWeight: 800, color: D.muted, letterSpacing: 0.7 }}>
                                <Box sx={{ width: 44 }}>ROW</Box><Box sx={{ flex: 1.3 }}>CANDIDATE</Box><Box sx={{ flex: 1.2 }}>VACANCY</Box><Box sx={{ width: 170 }}>STATUS</Box>
                            </Stack>
                            {prepared.slice(0, 60).map((r) => {
                                const c = r.status === 'ready' ? D.ok : r.status === 'duplicate' ? D.warn : D.bad;
                                return (
                                    <Stack key={r.line} direction="row" sx={{ alignItems: 'center', px: 1.8, py: 1, borderTop: `1px solid ${D.line}` }}>
                                        <Box sx={{ width: 44, color: D.muted, fontSize: 12 }}>{r.line}</Box>
                                        <Box sx={{ flex: 1.3, minWidth: 0 }}>
                                            <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: D.text }}>{r.name || '—'}</Typography>
                                            <Typography noWrap sx={{ fontSize: 11, color: D.muted }}>{r.email || r.phone || '—'}</Typography>
                                        </Box>
                                        <Box sx={{ flex: 1.2, minWidth: 0 }}><Typography noWrap sx={{ fontSize: 12.5, color: '#475569' }}>{r.vacancy?.title || '—'}</Typography></Box>
                                        <Box sx={{ width: 170 }}>
                                            <Chip size="small" label={r.status === 'ready' ? 'Ready' : r.note} sx={{ height: 22, maxWidth: 164, fontSize: 10.5, fontWeight: 700, bgcolor: `${c}22`, color: c, border: `1px solid ${c}55` }} />
                                        </Box>
                                    </Stack>
                                );
                            })}
                        </Box>
                        {counts.duplicate > 0 && counts.ready > 0 && (
                            <Typography sx={{ fontSize: 12, color: D.ok, mb: 1, fontWeight: 600 }}>
                                {counts.duplicate} already added {counts.duplicate === 1 ? 'is' : 'are'} skipped. Only the {counts.ready} missing candidate{counts.ready === 1 ? '' : 's'} will be imported.
                            </Typography>
                        )}
                        {noEmailCount > 0 && <Typography sx={{ fontSize: 12, color: D.warn, mt: 1, fontWeight: 600 }}>{noEmailCount} candidate{noEmailCount === 1 ? ' has' : 's have'} no email. A placeholder address (phone number @{PLACEHOLDER_DOMAIN}) will be saved for {noEmailCount === 1 ? 'it' : 'them'} because the server needs a unique one. Replace it later when you have the real email.</Typography>}
                        {prepared.length > 60 && <Typography sx={{ fontSize: 12, color: D.muted, mt: 1 }}>Showing the first 60 of {prepared.length} rows. All rows are processed.</Typography>}
                        {importing && (
                            <Box sx={{ mt: 2.2 }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: D.text, mb: 0.8 }}>Importing… {progress}%</Typography>
                                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 5, bgcolor: '#EEF1F6', '& .MuiLinearProgress-bar': { borderRadius: 5, background: `linear-gradient(90deg, ${D.accent}, ${D.accent2})` } }} />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Footer */}
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2, px: 3, py: 1.8, borderTop: `1px solid ${D.line}`, bgcolor: '#FBFAFE' }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                    {file && !result && (
                        <>
                            <DescriptionRoundedIcon sx={{ fontSize: 17, color: D.accent2 }} />
                            <Typography noWrap sx={{ fontSize: 12.5, color: D.muted, maxWidth: 320 }}>{file.name} · {fmtSize(file.size)}{sheets.length > 1 ? ` · ${sheets[sheetIdx]?.name}` : ''}</Typography>
                        </>
                    )}
                </Stack>
                <Stack direction="row" spacing={1}>
                    {step > 0 && !result && <Button onClick={() => setStep(step - 1)} disabled={importing} sx={btnGhost}>Back</Button>}
                    {result ? (
                        <Button onClick={onClose} sx={btnSolid}>Done</Button>
                    ) : (
                        <>
                            <Button onClick={onClose} disabled={importing} sx={btnGhost}>Cancel</Button>
                            <Button onClick={next} disabled={!canContinue} endIcon={importing ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <ChevronRightRoundedIcon />} sx={btnSolid}>
                                {step === 2 ? (importing ? 'Importing…' : `Import ${counts.ready} candidate${counts.ready === 1 ? '' : 's'}`) : 'Continue'}
                            </Button>
                        </>
                    )}
                </Stack>
            </Stack>
            </Box>

        </Dialog>
    );
}
